import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSystem, useServers } from "@/lib/hooks";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { StackBadge } from "@/components/StackBadge";
import { UploadCloud, FileArchive, CheckCircle2, AlertTriangle, Rocket, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function UploadPage() {
  const nav = useNavigate();
  const { data: sys } = useSystem();
  const { data: servers } = useServers();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [detected, setDetected] = useState(null); // Site
  const [name, setName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [serverId, setServerId] = useState("");   // "" = simulation
  const [envRows, setEnvRows] = useState([{ k: "", v: "" }]);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    if (detected) {
      setName(detected.name || "");
      setSubdomain(detected.subdomain || "");
      setServerId(detected.server_id || "");
    }
  }, [detected]);

  const pickFile = () => inputRef.current?.click();

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleFile = (f) => {
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please select a .zip file");
      return;
    }
    setFile(f);
    doUpload(f);
  };

  const doUpload = async (f) => {
    setUploading(true);
    setProgress(0);
    setDetected(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/sites/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          const pct = Math.round((ev.loaded * 100) / (ev.total || 1));
          setProgress(pct);
        },
      });
      setDetected(data.site);
      toast.success(`Detected ${data.site.stack_type}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "upload failed");
    } finally {
      setUploading(false);
    }
  };

  const patchSite = async () => {
    const env = {};
    envRows.forEach(({ k, v }) => {
      if (k.trim()) env[k.trim()] = v;
    });
    const body = { name, subdomain, env, server_id: serverId || null };
    const { data } = await api.patch(`/sites/${detected.id}`, body);
    setDetected(data);
  };

  const deploy = async () => {
    setDeploying(true);
    try {
      await patchSite();
      await api.post(`/sites/${detected.id}/deploy`);
      toast.success("Deploy started");
      nav(`/sites/${detected.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "deploy failed");
    } finally {
      setDeploying(false);
    }
  };

  const cancel = async () => {
    if (detected) {
      try { await api.delete(`/sites/${detected.id}`); } catch (_e) { /* ignore */ }
    }
    setDetected(null);
    setFile(null);
    setProgress(0);
    setEnvRows([{ k: "", v: "" }]);
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Upload</h1>
        <p className="text-muted-foreground mt-1">
          Drop a ZIP of your project. emhost will detect the stack and generate Docker + Caddy configs.
        </p>
      </header>

      {!detected && (
        <Card
          data-testid="upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={pickFile}
          className={`cursor-pointer border-2 border-dashed rounded-2xl p-12 text-center transition-colors ${
            dragOver
              ? "border-[hsl(var(--ring))] bg-[hsl(var(--surface-2))]/60"
              : "border-border surface-1 hover:bg-[hsl(var(--surface-2))]/40"
          }`}
        >
          <div className="mx-auto h-14 w-14 rounded-2xl surface-2 grid place-items-center mb-4">
            <UploadCloud className="h-6 w-6" />
          </div>
          <div className="text-lg font-semibold">Drop your ZIP here</div>
          <div className="text-sm text-muted-foreground mt-1">
            or click to browse. Supports static sites, React SPAs, FastAPI+React fullstack, and custom Docker projects.
          </div>
          <div className="mt-4 flex justify-center gap-2">
            <StackBadge type="static" />
            <StackBadge type="react_spa" />
            <StackBadge type="fullstack_farm" />
            <StackBadge type="custom_docker" />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            data-testid="upload-file-input"
          />

          {uploading && (
            <div className="mt-6 max-w-md mx-auto space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <FileArchive className="h-4 w-4" />
                <span className="truncate">{file?.name}</span>
              </div>
              <Progress data-testid="upload-progress-bar" value={progress} />
              <div className="text-xs text-muted-foreground">{progress}%</div>
            </div>
          )}
        </Card>
      )}

      {detected && (
        <div data-testid="upload-detect-modal" className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-3 p-6 border-border">
            <div className="flex items-center gap-3">
              {detected.stack_type === "unknown" ? (
                <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: "hsl(0 72% 30%)" }}>
                  <AlertTriangle className="h-5 w-5" />
                </div>
              ) : (
                <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: "hsl(173 80% 22%)" }}>
                  <CheckCircle2 className="h-5 w-5" />
                </div>
              )}
              <div>
                <div className="text-lg font-semibold">
                  {detected.stack_type === "unknown" ? "Could not classify this ZIP" : "Stack detected"}
                </div>
                <div className="text-sm text-muted-foreground">{detected.detect_notes}</div>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <StackBadge type={detected.stack_type} />
            </div>

            <div className="mt-5">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Evidence</div>
              <ul className="space-y-1 text-sm">
                {(detected.detect_evidence || []).map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                    <span className="font-mono text-[13px]">{e}</span>
                  </li>
                ))}
                {(detected.detect_evidence || []).length === 0 && (
                  <li className="text-muted-foreground text-sm">No positive markers found.</li>
                )}
              </ul>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Site name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="upload-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sub">Subdomain</Label>
                <div className="flex items-center gap-1">
                  <Input id="sub" value={subdomain} onChange={(e) => setSubdomain(e.target.value)} data-testid="upload-subdomain-input" />
                  <span className="text-muted-foreground text-sm whitespace-nowrap">.{sys?.base_domain || "emhost.local"}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-1.5">
              <Label htmlFor="target">Deploy target</Label>
              <select
                id="target"
                data-testid="upload-server-select"
                value={serverId}
                onChange={(e) => setServerId(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm"
              >
                <option value="">Simulation (no server) — preview only</option>
                {(servers || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {s.username}@{s.host}:{s.port}
                    {s.reachable ? " ✓" : " (unreachable)"}
                  </option>
                ))}
              </select>
              {(servers || []).length === 0 && (
                <div className="text-[11px] text-muted-foreground">
                  No servers registered yet. Add one on the <a href="/servers" className="underline">Servers</a> page for real deploys.
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <Label>Environment variables</Label>
                <Button variant="ghost" size="sm" className="gap-1.5"
                        onClick={() => setEnvRows((r) => [...r, { k: "", v: "" }])}
                        data-testid="upload-add-env-button">
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
              <div data-testid="upload-env-editor" className="space-y-2">
                {envRows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2">
                    <Input
                      placeholder="KEY"
                      value={row.k}
                      onChange={(e) => setEnvRows((r) => r.map((x, i) => i === idx ? { ...x, k: e.target.value } : x))}
                      className="col-span-5 font-mono"
                    />
                    <Input
                      placeholder="value"
                      value={row.v}
                      onChange={(e) => setEnvRows((r) => r.map((x, i) => i === idx ? { ...x, v: e.target.value } : x))}
                      className="col-span-6 font-mono"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="col-span-1"
                      onClick={() => setEnvRows((r) => r.filter((_, i) => i !== idx).length ? r.filter((_, i) => i !== idx) : [{ k: "", v: "" }])}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-2">
              <Button variant="ghost" onClick={cancel}>Discard</Button>
              <Button
                data-testid="upload-deploy-button"
                onClick={deploy}
                disabled={deploying || detected.stack_type === "unknown"}
                className="gap-2"
              >
                <Rocket className="h-4 w-4" />
                {deploying ? "Deploying…" : "Save & Deploy"}
              </Button>
            </div>
          </Card>

          <Card className="lg:col-span-2 p-6 border-border">
            <div className="text-sm text-muted-foreground uppercase tracking-wide">What happens next</div>
            <ol className="mt-3 space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full surface-2 grid place-items-center text-[11px]">1</div>
                <div>emhost generates a <code className="font-mono">Dockerfile</code>, <code className="font-mono">docker-compose.yml</code> and a Caddy site block.</div>
              </li>
              <li className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full surface-2 grid place-items-center text-[11px]">2</div>
                <div>On Deploy, containers are built and started (Live mode) or simulated (this preview).</div>
              </li>
              <li className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full surface-2 grid place-items-center text-[11px]">3</div>
                <div>Caddy routes <code className="font-mono">{subdomain || "your-site"}.{sys?.base_domain || "emhost.local"}</code> to the container with auto HTTPS.</div>
              </li>
            </ol>
          </Card>
        </div>
      )}
    </div>
  );
}
